import React, { useState, useContext, useEffect } from "react";
import { Badge, Box, Button, Collapsible, HStack, Input, InputGroup, Popover, Portal, Separator, SimpleGrid, Stack, Tabs, Text } from "@chakra-ui/react";
import { LuUsers, LuPlus, LuBan, LuUser, LuMail, LuBookUser, LuShield, LuText, LuSearch, LuSquarePen, LuCircleCheck, LuCircleX } from "react-icons/lu";
import { useAppStore } from "../../../../context/store";
import { Tooltip } from "./../../../ui/tooltip";
import Type from "../../../../utilies/Types";
import { serviceGetTeam } from "../../../../api/services/team";
import { showToast } from "../../../../utilies/Utility";
import { serviceEditUsers, serviceGetUsers } from "../../../../api/services/user";
import UsersAndTeamManagementNewTeamDialog from "./UsersAndTeamManagementNewTeamDialog";
import UsersAndTeamManagementNewUserDialog from "./UsersAndTeamManagementNewUserDialog";

function UsersAndTeamManagement() {
  // state
  const userType = useAppStore((s) => s.userType);
  const userData = useAppStore((s) => s.userData);
  const users = useAppStore((s) => s.users);
  const teams = useAppStore((s) => s.teams);
  const isUserManagementOpen = useAppStore((s) => s.isUserManagementOpen);

  // setters
  const setShowNewUserDialog = useAppStore.getState().setShowNewUserDialog;
  const setShowNewTeamDialog = useAppStore.getState().setShowNewTeamDialog;
  const setTeams = useAppStore.getState().setTeams;
  const setSelectTeamForEdit = useAppStore.getState().setSelectTeamForEdit;
  const setSelectUserForEdit = useAppStore.getState().setSelectUserForEdit;
  const setUsers = useAppStore.getState().setUsers;
  const setIsUserManagementOpen = useAppStore.getState().setIsUserManagementOpen;

  const [usersFilter, setUsersFilter] = useState({});

  const getTeamFromServer = () => {
    const formData = new FormData();
    if (userType.includes(Type.ROLE.ADMIN)) {
      formData.append("team_admin", userData?.user?.username);
    }

    serviceGetTeam(formData)
      .then((res) => {
        if (res.data.status == "failed") {
          showToast(res.data.message, Type.NOTIFICATION.ERROR);
        } else {
          setTeams(res.data.teams);
        }
      })
      .catch((err) => {
        console.error("Error", err);
      });
  };

  useEffect(() => {
    getTeamFromServer();
  }, []);

  useEffect(() => {
    if (users) {
      setUsersFilter(users);
    }
  }, [users]);

  const changeUserActive = (isActive, item) => {
    const formData = new FormData();
    let temp = {
      username: item.username,
      is_active: isActive,
    };
    formData.append("data", JSON.stringify(temp));
    serviceEditUsers(formData)
      .then((res) => {
        if (res.data.status == "failed") {
          showToast(res.data.message, Type.NOTIFICATION.ERROR);
        } else {
          showToast("User updated", Type.NOTIFICATION.SUCCESS);
          getUsers();
        }
      })
      .catch((err) => {
        console.error("Error", err);
        showToast(err.message, Type.NOTIFICATION.ERROR);
      });
  };

  const getUsers = () => {
    serviceGetUsers()
      .then((res) => {
        setUsers(res.data);
      })
      .catch((err) => {
        console.error("Error", err);
      });
  };

  return (
    <>
      <Popover.Root
        positioning={{ placement: "right-end" }}
        open={isUserManagementOpen}
        onOpenChange={(e) => {
          setIsUserManagementOpen(e.open);
        }}
        closeOnInteractOutside={false}
      >
        <Popover.Trigger asChild>
          <Button size="lg" p={2} bg={isUserManagementOpen ? "green" : "white"} color={isUserManagementOpen ? "white" : "MenuText"} width={"45px"} height={"45px"}>
            <Tooltip content="User Management">
              <LuUsers color={isUserManagementOpen ? "white" : "MenuText"} />
            </Tooltip>
          </Button>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content width={"500px"} height={"80vh"} zIndex={1001} borderRadius={10}>
              <Popover.Arrow />
              <Popover.Header>
                <Popover.Title fontSize={"xl"} width={"full"}>
                  User & Teams
                </Popover.Title>
              </Popover.Header>
              <Popover.Body p={2}>
                <Tabs.Root defaultValue="users">
                  <Tabs.List>
                    <Tabs.Trigger value="users">
                      <LuUser />
                      Users
                    </Tabs.Trigger>
                    <Tabs.Trigger value="teams">
                      <LuUsers />
                      Teams
                    </Tabs.Trigger>
                  </Tabs.List>
                  <Tabs.Content value="users" pt={0} overflowY={"auto"} height={"calc(80vh - 100px)"}>
                    {userType.includes(Type.ROLE.SUPER_ADMIN) && (
                      <SimpleGrid columns={6} gap={1} my={2}>
                        <Button variant="outline" size={"2xl"} p={0} height={"65px"} onClick={() => setShowNewUserDialog(true)}>
                          <Tooltip content="New User">
                            <LuPlus size={20} />
                          </Tooltip>
                        </Button>
                      </SimpleGrid>
                    )}

                    <InputGroup
                      flex="1"
                      startElement={<LuSearch />}
                      onChange={(v) => {
                        let a = Object.values(users).filter((user) => {
                          const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
                          return (
                            user.username.toLowerCase().includes(v.target.value.toLowerCase()) ||
                            user.email.toLowerCase().includes(v.target.value.toLowerCase()) ||
                            fullName.includes(v.target.value.toLowerCase())
                          );
                        });

                        setUsersFilter(a);
                      }}
                    >
                      <Input placeholder="Find User" />
                    </InputGroup>
                    {Object.values(usersFilter).map((item, index) => (
                      <Collapsible.Root key={index} borderWidth={"1px"} px={2} mt={2}>
                        <Collapsible.Trigger paddingY="3" cursor={"pointer"} width={"full"} position={"relative"}>
                          <Box position={"absolute"} top={2} right={0}>
                            {item.is_active ? <LuCircleCheck color="green" /> : <LuCircleX color="red" />}
                          </Box>
                          <HStack gap={2}>
                            <LuMail />
                            <Text>Email: {item.email}</Text>
                          </HStack>
                          <HStack gap={2}>
                            <LuBookUser />
                            <Text>Username: {item.username}</Text>
                          </HStack>
                          <HStack gap={2}>
                            <LuUser />
                            <Text>
                              Name: {item.first_name} {item.last_name}
                            </Text>
                          </HStack>
                          <HStack gap={2}>
                            <LuShield />
                            <Text>Roles:</Text>
                            <Stack direction="row">
                              {item.roles
                                .filter((role) => [Type.ROLE.ADMIN, Type.ROLE.ANNOTATOR, Type.ROLE.REVIEWER,Type.ROLE.VIEWER, Type.ROLE.SUPER_ADMIN, Type.ROLE.DIGITIZER].includes(role))
                                .map((role, index) => (
                                  <Badge key={index} variant="outline">
                                    {role}
                                  </Badge>
                                ))}
                            </Stack>
                          </HStack>
                        </Collapsible.Trigger>
                        <Collapsible.Content>
                          {userType.includes(Type.ROLE.SUPER_ADMIN) && (
                            <Box padding="2" borderWidth="1px" mb={2}>
                              <HStack gap={0} width={"full"}>
                                {item.is_active ? (
                                  <Button
                                    bg={"transparent"}
                                    color={"red"}
                                    m={0}
                                    p={0}
                                    onClick={() => {
                                      changeUserActive(false, item);
                                    }}
                                  >
                                    <Tooltip content={"Suspend User"}>
                                      <LuBan />
                                    </Tooltip>
                                  </Button>
                                ) : (
                                  <Button
                                    bg={"transparent"}
                                    color={"red"}
                                    m={0}
                                    p={0}
                                    onClick={() => {
                                      changeUserActive(true, item);
                                    }}
                                  >
                                    <Tooltip content={"Active User"}>
                                      <LuCircleCheck color="green" />
                                    </Tooltip>
                                  </Button>
                                )}

                                <Button
                                  bg={"transparent"}
                                  color={"MenuText"}
                                  m={0}
                                  p={0}
                                  onClick={() => {
                                    setSelectUserForEdit(item);
                                    setShowNewUserDialog(true);
                                  }}
                                >
                                  <Tooltip content={"Edit User"}>
                                    <LuSquarePen />
                                  </Tooltip>
                                </Button>
                              </HStack>
                            </Box>
                          )}
                        </Collapsible.Content>
                      </Collapsible.Root>
                    ))}
                  </Tabs.Content>
                  <Tabs.Content value="teams" pt={0} overflowY={"auto"} height={"calc(80vh - 100px)"}>
                    {userType.includes(Type.ROLE.SUPER_ADMIN) && (
                      <>
                        <SimpleGrid columns={6} gap={1} my={2}>
                          <Button variant="outline" size={"2xl"} p={0} height={"65px"} onClick={() => setShowNewTeamDialog(true)}>
                            <Tooltip content="New Team">
                              <LuPlus size={20} />
                            </Tooltip>
                          </Button>
                        </SimpleGrid>
                        <Separator mt={3} />
                      </>
                    )}
                    {teams.map((item, index) => (
                      <Collapsible.Root key={index} borderWidth={"1px"} px={3} mt={2}>
                        <Collapsible.Trigger paddingY="3" cursor={"pointer"} width={"full"}>
                          <HStack gap={2} mb={2}>
                            <LuText />
                            <Text>Name: {item.team_name ? item.team_name : "No Name"}</Text>
                          </HStack>
                          <HStack gap={2} mb={2}>
                            <LuUsers />
                            <Text>Admin:</Text>
                            <Badge variant="outline">{item.team_admin}</Badge>
                          </HStack>
                          <HStack gap={2} mb={2}>
                            <LuUsers />
                            <Text>Members ({item.members.length}):</Text>
                            <Stack direction="row">
                              {item.members.map((item2, index) => (
                                <Badge key={index} variant="outline">
                                  {item2}
                                </Badge>
                              ))}
                            </Stack>
                          </HStack>
                        </Collapsible.Trigger>
                        <Collapsible.Content>
                          <Box padding="2" borderWidth="1px" mb={2}>
                            {userType.includes(Type.ROLE.SUPER_ADMIN) && (
                              <HStack gap={0} width={"full"}>
                                <Button
                                  bg={"transparent"}
                                  color={"MenuText"}
                                  m={0}
                                  p={0}
                                  onClick={() => {
                                    setSelectTeamForEdit(item);
                                    setShowNewTeamDialog(true);
                                  }}
                                >
                                  <Tooltip content={"Edit Team"}>
                                    <LuSquarePen />
                                  </Tooltip>
                                </Button>
                              </HStack>
                            )}
                            <HStack gap={2}>
                              <LuText />
                              <Text>Description: {item.description ? item.description : "Empty"}</Text>
                            </HStack>
                          </Box>
                        </Collapsible.Content>
                      </Collapsible.Root>
                    ))}
                  </Tabs.Content>
                </Tabs.Root>
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
      {(userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) && <UsersAndTeamManagementNewTeamDialog />}
      {(userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) && <UsersAndTeamManagementNewUserDialog />}
    </>
  );
}

export default UsersAndTeamManagement;
